import React, { useState } from "react";
import ReactMarkdown from "react-markdown";
import type { TopicImage } from "../types/common";
import { splitTopicPlaceholders } from "../utils/topicImages";

// 題目說明渲染(kanban 114216):
// chapterDesc 內的【圖片N】只是排版佔位,真正的圖檔在同一份 chapter payload 的
// images[]（{n, url, alt}）依 n 對應。這裡把「切段」(topicImages.ts,純函式、可測)
// 和「怎麼畫」(這個元件)分開 —— repo 沒裝 jsdom,元件本身進不了 vitest,
// 邏輯必須全押在純函式那邊,元件只負責照著切好的段落依序渲染。
//
// 四頁五處(BookPreview / Feedback / ClassFeedback / UploadEssay ×2)原本各自複製一份
// markdown 渲染 + components 覆寫表,這裡統一抽成一份共用元件與樣式表。
//
// 圖片載入失敗:改顯示 alt 文字,不是隱藏。
// 既有 chapterDescUrl(前台說明圖片網址,另一條與本卡並存的機制)的 onError 是
// display:none,那條路徑原樣保留、不沿用到這裡 —— 這裡的圖片失敗要讓使用者知道
// 「這裡本來有一張說明圖」,而不是整段消失不留痕跡。

interface TopicDescProps {
  desc: string;
  images?: TopicImage[];
  chapterName: string;
}

type MdChildren = { children?: React.ReactNode };

// 沿用 BookPreview.tsx 題目預覽 Popup 那份最完整的 components 覆寫表。
// UploadEssay.tsx 原本兩處只有 h2/h3/p/strong 四種、較陽春,統一改用這份完整表
// 屬樣式統一化(kanban 114216 順手拉齊,細節見卡片回報)。
const markdownComponents = {
  h1: ({ children }: MdChildren) => (
    <h1 className="text-2xl font-bold mb-4 text-gray-800">{children}</h1>
  ),
  h2: ({ children }: MdChildren) => (
    <h2 className="text-xl font-semibold mb-3 text-gray-800">{children}</h2>
  ),
  h3: ({ children }: MdChildren) => (
    <h3 className="text-lg font-semibold mb-2 text-gray-800">{children}</h3>
  ),
  p: ({ children }: MdChildren) => (
    <p className="mb-3 text-gray-700 leading-relaxed">{children}</p>
  ),
  strong: ({ children }: MdChildren) => (
    <strong className="font-semibold text-gray-800">{children}</strong>
  ),
  em: ({ children }: MdChildren) => <em className="italic text-gray-700">{children}</em>,
  ul: ({ children }: MdChildren) => (
    <ul className="list-disc list-inside mb-3 text-gray-700">{children}</ul>
  ),
  ol: ({ children }: MdChildren) => (
    <ol className="list-decimal list-inside mb-3 text-gray-700">{children}</ol>
  ),
  li: ({ children }: MdChildren) => <li className="mb-1">{children}</li>,
  blockquote: ({ children }: MdChildren) => (
    <blockquote className="border-l-4 border-blue-400 pl-4 italic text-gray-600 my-4">
      {children}
    </blockquote>
  ),
  code: ({ children }: MdChildren) => (
    <code className="bg-gray-100 px-1 py-0.5 rounded text-sm font-mono text-gray-800">
      {children}
    </code>
  ),
  pre: ({ children }: MdChildren) => (
    <pre className="bg-gray-100 p-3 rounded text-sm font-mono overflow-x-auto mb-3">
      {children}
    </pre>
  ),
};

// 四處複製貼上共通的前處理:確保 ###/## 後面有空格,否則部分內容不會被判成標題。
const preprocess = (text: string) =>
  text.replace(/^###([^#\n]+)/gm, "### $1").replace(/^##([^#\n]+)/gm, "## $1");

const TopicDesc: React.FC<TopicDescProps> = ({ desc, images, chapterName }) => {
  // 記錄哪些圖片(依 n)載入失敗,失敗的改渲染 alt 文字
  const [failedNs, setFailedNs] = useState<Set<number>>(new Set());

  if (!desc) return null;

  const segments = splitTopicPlaceholders(desc, images);

  return (
    <>
      {segments.map((seg, idx) => {
        if (seg.type === "text") {
          return (
            <ReactMarkdown key={idx} components={markdownComponents}>
              {preprocess(seg.text)}
            </ReactMarkdown>
          );
        }

        if (seg.type === "placeholder") {
          // 未綁定的佔位(素材庫漏綁,或原本就沒有對應的圖):防禦性原樣顯示文字
          return <span key={idx}>{seg.raw}</span>;
        }

        const { img } = seg;
        if (failedNs.has(img.n)) {
          return (
            <p key={idx} className="text-gray-500 text-sm">
              {img.alt ?? "（圖片載入失敗）"}
            </p>
          );
        }

        return (
          <img
            key={idx}
            src={img.url}
            alt={img.alt ?? undefined}
            className="w-full max-w-2xl mx-auto rounded-md border border-gray-200 shadow-sm"
            loading="lazy"
            onError={() => {
              console.warn("題目圖片載入失敗:", chapterName, img.url);
              setFailedNs((prev) => new Set(prev).add(img.n));
            }}
          />
        );
      })}
    </>
  );
};

export default TopicDesc;
